import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import { computed, extendObservable } from 'mobx';
import moment from 'moment';
import NotificationService from '../../../core/services/NotificationService';
import { isRequired } from '../../../core/validations';
import { browserHistory } from 'react-router';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';
import { groupBy, head } from 'lodash';

class BillFormService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      contract: { products: [] },
      total: computed(() => {
        const products = this.form.getValue('products') || [];
        return products.reduce((total, product) => {
          if (product.discount) {
            const totalValue = !!product.amount
              ? Number(product.amount) * Number(product.unitPrice)
              : Number(product.unitPrice);
            return total + (product.discount ? totalValue - totalValue * (product.discount / 100) : totalValue);
          }
          return (
            total + (product.amount ? Number(product.amount) * Number(product.unitPrice) : Number(product.unitPrice))
          );
        }, 0);
      }),
    });
    this.form.validations = {
      contract: [isRequired],
      dueDate: [isRequired],
      referenceDateFrom: [isRequired],
      referenceDateTo: [isRequired],
    };
    this.init();
  }

  load = (billId) => {
    this.billId = billId;
    this.fetch.fetch({ url: `/bills/${this.billId}`, apiVersion: 'v2' }).then(() => {
      if (this.fetch.data) {
        this.contract.products = [
          ...this.fetch.data.contract.products.map((product) => ({
            ...product,
            value: product.hs_sku,
            label: product.description,
          })),
          { value: 'others', label: 'Others' },
        ];
        this.form.setInitialValues({
          ...this.fetch.data,
          products: this.fetch.data.products.map(({ hs_sku, description, ...rest }) => {
            const contractProduct = this.contract.products.find(
              (contractProduct) => hs_sku && contractProduct.hs_sku === hs_sku
            );
            return {
              ...rest,
              hs_sku: hs_sku || 'others',
              name: hs_sku === 'others' ? 'Others' : description,
              ...(hs_sku === 'others' && {
                description,
              }),
              ...(contractProduct && contractProduct.installments
                ? { installments: contractProduct.installments }
                : {}),
            };
          }),
          contract: this.fetch.data.contract.id,
          socialReason: this.fetch.data.contract.socialReason,
          country: this.fetch.data.contract.country,
        });
      }
    });
  };

  init = () => {
    this.form.setInitialValues({
      products: [],
    });
    this.form.reset();
    this.billId = null;
  };

  loadContract = (contractId) => {
    this.fetch
      .fetch({
        url: `/contracts/${contractId}`,
        apiVersion: 'v2',
      })
      .then(() => {
        if (this.fetch.data) {
          const products = this.fetch.data.products.filter((product) =>
            moment(product.beginningOfTerm).utc().isSameOrBefore(moment().utc().startOf('month').startOf('day'))
          );
          const grouped = groupBy(products, 'hs_sku');
          const unique = Object.keys(grouped).map((hs_sku) => {
            return grouped[hs_sku].length > 1
              ? head(
                  grouped[hs_sku].sort((p1, p2) => {
                    const beginningOfTermP1 = moment(p1.beginningOfTerm);
                    const beginningOfTermP2 = moment(p2.beginningOfTerm);
                    if (beginningOfTermP1.isBefore(beginningOfTermP2)) {
                      return 1;
                    } else {
                      return -1;
                    }
                  })
                )
              : head(grouped[hs_sku]);
          });
          this.contract.products = [
            ...unique.map((product) => ({
              ...product,
              value: product.hs_sku,
              label: product.description,
            })),
            { value: 'others', label: 'Others' },
          ];
          this.form.setValue('contract', contractId);
          this.form.setValue('socialReason', this.fetch.data.socialReason);
          this.form.setValue(
            'dueDate',
            moment().isAfter(moment().set('D', this.fetch.data.dueDay))
              ? moment().set('D', this.fetch.data.dueDay).add(1, 'M').format()
              : moment().set('D', this.fetch.data.dueDay).format()
          );
          this.form.setValue('country', this.fetch.data.country);
        }
      });
  };

  handleContractChange = (contractId) => {
    if (this.form.getValue('products').length) {
      ConfirmationDialogService.show(
        '',
        'Do you really want to change the contract? If you do this, the bill’s products will be removed.',
        () => {
          this.form.setValue('products', []);
          this.form.setValue('socialReason', null);
          this.form.setValue('dueDate', null);
          this.form.setValue('contract', contractId);
          if (contractId) {
            this.loadContract(contractId);
          }
        }
      );
    } else {
      this.form.reset();
      this.form.setValue('contract', contractId);
      if (contractId) {
        this.loadContract(contractId);
      }
    }
  };

  handleChange = (path, value) => {
    if (path === 'contract') {
      this.handleContractChange(value);
    } else {
      if (path === 'bankBillFile') {
        this.form.setValue('bankBillUrl', null);
      }
      this.form.setValue(path, value);
    }
  };

  handleSubmit = () => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }
    if (!this.form.getValue('products').length) {
      NotificationService.addNotification('A product is required to create a bill', 'error');
      return;
    }
    this.submit
      .fetch({
        method: this.billId ? 'put' : 'post',
        url: this.billId ? `/bills/${this.billId}` : '/bills',
        apiVersion: 'v2',
        body: {
          contract: this.form.getValue('contract'),
          dueDate: moment(this.form.getValue('dueDate')).utc().endOf('day'),
          referenceDateFrom: moment(this.form.getValue('referenceDateFrom')).utc().startOf('day'),
          referenceDateTo: moment(this.form.getValue('referenceDateTo')).utc().endOf('day'),
          bankBillUrl: this.form.getValue('bankBillUrl'),
          products: this.form.getValue('products').map((product) => ({
            hs_sku: product.hs_sku,
            description: product.hs_sku !== 'others' ? product.name : product.description,
            ...(product.amount ? { amount: Number(product.amount) } : {}),
            unitPrice: Number(product.unitPrice),
            ...(product.discount && { discount: Number(product.discount) }),
            ...(product.installmentNumber ? { installmentNumber: Number(product.installmentNumber) } : {}),
          })),
        },
      })
      .then(() => {
        if (this.submit.data) {
          NotificationService.addNotification(`Bill ${this.billId ? 'updated' : 'created'} successfully.`, 'success');
          browserHistory.push('/billing');
        }
        if (this.submit.error) {
          NotificationService.addNotification(`Error ${this.billId ? 'updating' : 'creating'} Bill.`, 'error');
        }
      });
  };

  handleAction = (action, data) => {
    switch (action) {
      case 'open':
        this.handleOpen(data);
        break;
      case 'send':
        this.handleSendToClient(data);
        break;
      case 'download':
        this.handleDownload(data);
        break;
      case 'gift':
        this.handleNoCharge(data);
        break;
      default:
        break;
    }
  };

  handleNoCharge = (bill) => {
    ConfirmationDialogService.show(
      'No Charge Bill',
      `Would you really like change the bill ${bill.billId} status to 'No Charge'?`,
      async () => {
        this.submit
          .fetch({
            method: 'put',
            url: `/bills/${bill.id}/no-charge`,
            apiVersion: 'v2',
          })
          .then(() => {
            if (this.submit.data) {
              NotificationService.addNotification(`Bill marked as no charge successfully.`, 'success');
              this.load(bill.id);
            }
            if (this.submit.error) {
              NotificationService.addNotification(`Error marking Bill as no charge.`, 'error');
            }
          });
      }
    );
  };

  handleSendToClient = (bill) => {
    ConfirmationDialogService.show(
      `Bill ${bill.billId}`,
      `Would you really like to send the bill ${bill.billId} to client?`,
      () => {
        return new Promise((resolve) => {
          this.submit
            .fetch({
              method: 'put',
              url: `/bills/${bill.id}/send-to-client`,
              apiVersion: 'v2',
            })
            .then(() => {
              if (this.submit.data) {
                if (bill) {
                  NotificationService.addNotification(`Bill sent to client successfully.`, 'success');
                } else {
                  NotificationService.addNotification(`Bills sent to client successfully.`, 'success');
                }
                this.load(bill.id);
              }
              if (this.submit.error) {
                if (bill) {
                  NotificationService.addNotification(`Error sending Bill to client.`, 'error');
                } else {
                  NotificationService.addNotification(`Error sending Bills to client.`, 'error');
                }
              }
              resolve();
            });
        });
      }
    );
  };

  handleDownload = (billUrl) => {
    const filePath = `${process.env.REACT_APP_FILES_URL}/${billUrl}`;
    const link = document.createElement('a');
    link.href = filePath;
    link.download = filePath.split('/').pop();
    link.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(filePath);
      link.remove();
    }, 500);
  };

  handleOpen = (bill) => {
    ConfirmationDialogService.show('Open Bill', `Would you really like to reopen the bill ${bill.billId}?`, () => {
      this.submit
        .fetch({
          method: 'put',
          url: `/bills/${bill.id}/reopen`,
          apiVersion: 'v2',
        })
        .then(() => {
          if (this.submit.data) {
            NotificationService.addNotification(`Bill opened successfully.`, 'success');
            this.load(bill.id);
          }
          if (this.submit.error) {
            NotificationService.addNotification(`Error opening Bill.`, 'error');
          }
        });
    });
  };
}

let billFormService = new BillFormService();

export default billFormService;
